﻿import React, { useState, useEffect } from 'react';
import { OpticalRotationBenchSheet } from '../models/optical-rotation-bench-sheet';
import { BenchSheetTechnologyType, DocumentType, NoteType, Entities } from '../models/enums';
import SecondaryBenchSheetInstrument from './SecondaryBenchSheetInstrument';
import BenchSheetHeaderComponent from './BenchSheetHeaderComponent';
import QCCheckComponent from './QCCheckComponent';
import BenchSheetData from './BenchSheetData';
import { FieldUpdate } from '../models/field-update';
import OpticalRotationInstruments from './OpticalRotationInstruments';
import SamplePreparationsComponent from './SamplePreparations/SamplePreparationsComponent';
import OpticalRotationDataData from './OpticalRotationDataData';
import { CrudFunctions } from '../utilities/crud-functions';
import { UserDataStore } from '../store/userDataStore';
import NoteViewer from './NoteViewer';
import { ItemBasicInformation } from '../models/item-basic-information';
import { DocumentationConstants } from '../store/documentation-constants';
import BenchSheetToolbar from './BenchSheetToolbar';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import TicketLogEntryForm from './TicketLogEntryForm';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import DmaFooter from './DmaFooter';

import { Popup as DialogPopup } from 'devextreme-react/popup';
import RadioGroup from 'devextreme-react/radio-group';
import TextBox from 'devextreme-react/text-box';
import NumberBox from 'devextreme-react/number-box';

let key = 1;

//temp
const methodTypes = [
    { id: 1, name: "Spec Rotation" },
    { id: 2, name: "Angular Rotation" },
    { id: 3, name: "OBS Rotation" },
]

const basisTypes = [
    { id: 1, name: "Dried" },
    { id: 2, name: "Anhydrous" },
    { id: 3, name: "As Is" }
]

export default function OpticalRotationBenchSheetForm() {
    const [canEdit, setCanEdit] =
        useState<boolean>(false);

    const [benchSheet, setBenchSheet] =
        useState<OpticalRotationBenchSheet>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [ticketLogEntryDialogVisible, setTicketLogEntryDialogVisible] =
        useState<boolean>();

    const [itemBasicInitInformation, setItemBasicInitInformation] =
        useState<ItemBasicInformation>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [dataLoaded, setDataLoaded] =
        useState<boolean>();

    useEffect(() => {
        setAuthorizations();
        getAllData();
    }, []);

    const parts = window.location.href.split('/');
    const benchSheetId = parts[parts.length - 1];

    key++;

    return (
        <div className="pw-form-container">
            <div className="pw-content">

                <BenchSheetToolbar title="OR Bench Sheet"
                    subtitle="Optical Rotation"
                    auditRowPointer={benchSheetId} entity={Entities.OrBenchSheets}
                    technologyId={BenchSheetTechnologyType.OpticalRotation}
                    documentationId={DocumentationConstants.ORBenchSheets}
                    showItemInformation={showBenchSheetInformationDialog}
                    showTicketLog={showTicketLogEntryDialog} />

                <BenchSheetHeaderComponent benchSheetId={benchSheetId} readOnly={!canEdit} />

                <div>
                    <div className='pw-comp-row'>
                        <div className="pw-form-unit">
                            <div className="pw-form-unit-label pw-margin-bottom-05">Instrument Type</div>
                            <RadioGroup id="instrumentType" items={methodTypes}
                                displayExpr='name' valueExpr='id' layout="horizontal"
                                onValueChanged={onValueChanged}
                                value={benchSheet && benchSheet.instrumentType ? benchSheet.instrumentType : undefined} />
                        </div>
                    </div>

                    <div className='pw-comp-row pw-margin-top-1'>
                        <div className="pw-form-unit pw-comp-10">
                            <div className="pw-form-unit-label">Volatile Content %</div>
                            <NumberBox id="volatileContentPercent" readOnly={!canEdit}
                                placeholder='NA' className="textbox"
                                onValueChanged={onValueChanged}
                                value={benchSheet && benchSheet.volatileContentPercent ? benchSheet.volatileContentPercent : undefined} />
                        </div>

                        <div className="pw-form-unit pw-comp-30">
                            <div className="pw-form-unit-label">Drying Condition</div>
                            <TextBox id="dryingCondition" readOnly={!canEdit}
                                placeholder='NA' className="textbox"
                                onValueChanged={onValueChanged}
                                value={benchSheet && benchSheet.dryingCondition ? benchSheet.dryingCondition : undefined} />
                        </div>
                    </div>

                    <div className='pw-comp-row pw-margin-top-05'>
                        <div className="pw-form-unit pw-comp-10">
                            <div className="pw-form-unit-label">Temperature</div>
                            <NumberBox id="temperature" readOnly={!canEdit}
                                placeholder='NA' className="textbox"
                                onValueChanged={onValueChanged}
                                value={benchSheet && benchSheet.temperature ? benchSheet.temperature : undefined} />
                        </div>

                        <div className="pw-form-unit pw-comp-10">
                            <div className="pw-form-unit-label">Wavelength (nm)</div>
                            <NumberBox id="wavelength" readOnly={!canEdit}
                                placeholder='NA' className="textbox"
                                format="###0"
                                onValueChanged={onValueChanged}
                                value={benchSheet && benchSheet.wavelength ? benchSheet.wavelength : undefined} />
                        </div>
                    </div>

                    <div className='pw-comp-row'>
                        <div className="pw-form-unit">
                            <div className="pw-form-unit-label pw-margin-bottom-05">Basis Type</div>
                            <RadioGroup id="basisType" items={basisTypes}
                                displayExpr='name' valueExpr='id' layout="horizontal"
                                onValueChanged={onValueChanged}
                                value={benchSheet && benchSheet.basisType ? benchSheet.basisType : undefined} />
                        </div>
                    </div>
                </div>

                {benchSheet &&
                    <OpticalRotationInstruments benchSheet={benchSheet} readOnly={!canEdit} />
                }

                <div className="pw-margin-bottom-05">
                    <SamplePreparationsComponent benchSheetId={benchSheetId} diluent={false} />
                </div>

                <QCCheckComponent benchSheetId={benchSheetId} isReadOnly={!canEdit}
                    technologyId={BenchSheetTechnologyType.OpticalRotation}
                    datagridHeight='12rem' title='OR QC Checks' />

                <div className="pw-margin-top-05">
                    <OpticalRotationDataData benchSheetId={benchSheetId} isReadOnly={!canEdit} />
                </div>

                <div className="pw-comp-row-scroll-fixed-grid">
                    <BenchSheetData parentId={benchSheetId}
                        readOnly={!canEdit} canDelete={true}
                        technology={BenchSheetTechnologyType.OpticalRotation}
                        imageHandlerTitle="Optical Rotation Image"
                        documentType={DocumentType.General} />
                </div>

                <div className="pw-comp-row-scroll-fixed-grid">
                    <NoteViewer height='10rem' title="Bench Sheet Note"
                        noteType={NoteType.Method} parentId={benchSheetId} />
                </div>
            </div>

            <DmaFooter />

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInitInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {ticketLogEntryDialogVisible &&
                <DialogPopup visible={ticketLogEntryDialogVisible}
                    onHiding={hideTicketLogEntryDialog}
                    title="Ticket Log Entry"
                    width={'55rem'}
                    height={'45rem'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <TicketLogEntryForm formName="OR Bench Sheet" contextData={benchSheet}
                        onCancel={hideTicketLogEntryDialog}
                        onComplete={hideTicketLogEntryDialog}
                        key={`TLE${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }
        </div>
    );

    function setAuthorizations() {
        const user = UserDataStore.getCurrentUser();

        const canEditRoles = ["SA", "MF-A", "MF-L", "MF-T", "PP-A", "PP-L", "PP-T", "DIST-A", "BA-A", "QCP-A", "QCD-A", "RD-A", "RD-L", "RD-T"];
        const canEdit = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));

        setCanEdit(canEdit);
    }

    async function onValueChanged(e: any) {
        if (!dataLoaded)
            return;

        const caller = e.element.id as string;

        if (caller && benchSheet) {
            // Is all this needed? Its not being done this way anywhere else
            const bs = benchSheet as any;
            const currentValue = bs[caller];

            if (currentValue === e.value)
                return;
            else if (!currentValue && !e.value)
                return;
            else {
                setBenchSheet({ ...benchSheet, [caller]: e.value });

                let fieldUpdate: FieldUpdate = {
                    entity: "OpticalRotationBenchSheets",
                    field: caller,
                    value: e.value,
                    rowId: benchSheetId
                };

                await updateField(fieldUpdate)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Field");
                    });
            }
        }
    }

    function onMethodTypeValueChanged(e: any) {
        if (!dataLoaded)
            return;

        console.log("onMethodTypeValueChanged");
        console.log(e);

        const caller = e.element.id as string;

        if (caller && benchSheet) {
            // Is all this needed? Its not being done this way anywhere else
            const bs = benchSheet as any;
            const currentValue = bs[caller];

            if (currentValue === e.value)
                return;
            else if (!currentValue && !e.value)
                return;
            else {
                setBenchSheet({ ...benchSheet, [caller]: e.value });


            }
        }
    }

    function onBasisTypeValueChanged(e: any) {
        if (!dataLoaded)
            return;

        console.log("onBasisTypeValueChanged");
        console.log(e);

        const caller = e.element.id as string;

        if (caller && benchSheet) {
            // Is all this needed? Its not being done this way anywhere else
            const bs = benchSheet as any;
            const currentValue = bs[caller];

            if (currentValue === e.value)
                return;
            else if (!currentValue && !e.value)
                return;
            else {
                setBenchSheet({ ...benchSheet, [caller]: e.value });

            }
        }
    }

    //GET methods

    async function getBenchSheet() {
        await CrudFunctions.getData(`benchsheets/benchsheet/${benchSheetId}`)
            .then(data => {

                console.log("OR BS:")
                console.log(data);

                setBenchSheet(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Bench Sheet");
            });
    }

    // PUT functions

    async function updateField(fieldUpdate: FieldUpdate) {
        return CrudFunctions.putData("benchsheets/orbenchsheetfield", fieldUpdate);
    }

    // show/hide

    function showTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(true);
    }

    function hideTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(false);
    }

    function showBenchSheetInformationDialog() {
        if (benchSheet) {
            let itemBasicInitInformation: ItemBasicInformation = {
                rowPointer: benchSheet.benchSheetId,
                createTimestamp: benchSheet.createTimestamp,
                lastModifiedTimestamp: benchSheet.modifyTimestamp,
                createUser: benchSheet.createUser,
                modifyUser: benchSheet.modifyUser
            };

            setItemBasicInitInformation(itemBasicInitInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    async function getAllData() {
        getBenchSheet()
            .then(() => setDataLoaded(true));
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}
